import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DestructiveConfirm } from '@/components/ui/DestructiveConfirm'
import { BRAND_MINT } from '@/lib/utils'
import {
  createSupportEmailApi,
  deleteSupportEmailApi,
  getCountriesApi,
  getSupportEmailsApi,
  updateSupportEmailApi,
  type Country,
  type SupportEmail,
} from '@/lib/api'
import {
  LoaderIcon,
  MailIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  GlobeIcon,
  InboxIcon,
} from 'lucide-react'

export const Route = createFileRoute('/dashboard/admin/support-emails')({
  component: SupportEmailsPage,
})

function SupportEmailsPage() {
  const [emails, setEmails] = useState<SupportEmail[]>([])
  const [countries, setCountries] = useState<Country[]>([])
  const [loading, setLoading] = useState(true)

  // Add / edit form
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<SupportEmail | null>(null)
  const [email, setEmail] = useState('')
  const [countryCodes, setCountryCodes] = useState<string[]>([])
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  // Delete confirmation
  const [deleting, setDeleting] = useState<SupportEmail | null>(null)
  const [deletingLoading, setDeletingLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [emailsRes, countriesRes] = await Promise.all([
        getSupportEmailsApi(),
        getCountriesApi(),
      ])
      setEmails(emailsRes.data)
      setCountries(countriesRes.data)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load support emails')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const scopeLabel = (codes: string[]) => {
    if (!codes || codes.length === 0) return 'All countries'
    const names = codes.map((c) => countries.find((x) => x.iso_2 === c)?.country ?? c)
    if (names.length === 1) return names[0]
    if (names.length <= 3) return names.join(', ')
    return `${names.length} countries`
  }

  const openCreate = () => {
    setEditing(null)
    setEmail('')
    setCountryCodes([])
    setIsActive(true)
    setFormOpen(true)
  }

  const openEdit = (row: SupportEmail) => {
    setEditing(row)
    setEmail(row.email)
    setCountryCodes(row.country_codes ?? [])
    setIsActive(row.is_active)
    setFormOpen(true)
  }

  const handleSave = async () => {
    if (!email.trim()) {
      toast.error('Email is required')
      return
    }
    setSaving(true)
    try {
      if (editing) {
        await updateSupportEmailApi(editing.support_email_id, {
          email: email.trim(),
          country_codes: countryCodes,
          is_active: isActive,
        })
        toast.success('Support email updated.')
      } else {
        await createSupportEmailApi({
          email: email.trim(),
          country_codes: countryCodes,
        })
        toast.success('Support email added.')
      }
      setFormOpen(false)
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save support email')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    setDeletingLoading(true)
    try {
      await deleteSupportEmailApi(deleting.support_email_id)
      toast.success('Support email removed.')
      setDeleting(null)
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove support email')
    } finally {
      setDeletingLoading(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-0.5 shrink-0"
            style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
          />
          <div className="flex flex-1 items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold">Support emails</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Where &ldquo;Contact us&rdquo; form submissions are delivered. Emails can serve a
                group of countries, with the global inbox as the fallback.
              </p>
            </div>
            <Button size="sm" onClick={openCreate} className="shrink-0 gap-1.5">
              <PlusIcon className="size-4" />
              Add email
            </Button>
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
          <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
          <p className="ml-2 text-sm text-muted-foreground">Loading support emails…</p>
        </div>
      ) : emails.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
          <InboxIcon className="size-10 text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No support emails configured</p>
          <p className="text-sm text-muted-foreground">
            Add a global inbox so contact-form messages have a destination.
          </p>
          <Button variant="outline" size="sm" onClick={openCreate} className="mt-2 gap-1.5">
            <PlusIcon className="size-4" />
            Add email
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {emails.map((row) => (
            <div
              key={row.support_email_id}
              className="flex items-center gap-4 rounded-xl border border-border bg-card p-4"
            >
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <MailIcon className="size-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-foreground">{row.email}</p>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  <GlobeIcon className="size-3 shrink-0" />
                  <span className="truncate">{scopeLabel(row.country_codes)}</span>
                </p>
              </div>
              <span
                className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                  row.is_active
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {row.is_active ? 'Active' : 'Inactive'}
              </span>
              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(row)} aria-label={`Edit ${row.email}`}>
                  <PencilIcon className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleting(row)}
                  aria-label={`Remove ${row.email}`}
                  className="text-destructive hover:text-destructive"
                >
                  <TrashIcon className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / edit dialog */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? 'Edit support email' : 'Add support email'}
        description="Contact-form messages are routed to the matching country group first, then to the global inbox."
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setFormOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
              {saving ? <LoaderIcon className="size-4 animate-spin" /> : null}
              {editing ? 'Save changes' : 'Add email'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="support-email-address">Email address</Label>
            <Input
              id="support-email-address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="support@company.com"
              disabled={saving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="support-email-countries">Countries</Label>
            <select
              id="support-email-countries"
              multiple
              size={7}
              value={countryCodes}
              onChange={(e) => setCountryCodes(Array.from(e.target.selectedOptions, (o) => o.value))}
              disabled={saving}
              className="h-44 w-full overflow-y-auto rounded-lg border border-border bg-background px-2 py-2 text-sm outline-none focus:border-primary"
            >
              {countries
                .filter((c) => c.iso_2)
                .map((c) => (
                  <option key={c.country_id} value={c.iso_2 as string}>
                    {c.country}
                  </option>
                ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Select the countries this inbox serves (Ctrl/Cmd+click for multiple). Leave empty to
              make it a global fallback for all countries.
            </p>
            {countryCodes.length > 0 && (
              <button
                type="button"
                onClick={() => setCountryCodes([])}
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Clear — set as global
              </button>
            )}
          </div>

          {editing && (
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={saving}
                className="size-4 rounded border-border accent-primary"
              />
              Active (receive contact-form messages)
            </label>
          )}
        </div>
      </Dialog>

      {/* Delete confirmation */}
      <DestructiveConfirm
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Remove support email?"
        description={
          deleting
            ? `This will remove ${deleting.email} (${scopeLabel(deleting.country_codes)}). Contact-form messages will no longer be delivered to it.`
            : ''
        }
        confirmLabel="Remove"
        onConfirm={handleDelete}
        loading={deletingLoading}
      />
    </div>
  )
}